import { useEffect, useState } from 'react';
import { AppLayout } from '../../../components/layout';
import { Button, Card, Alert, Select } from '../../../components/core';
import { http } from '../../../lib/api';
import { useUiStore } from '../../../store/ui';

const NAV = [
  { active: 'dashboard', items: [{ to: '/dashboard', label: 'Dashboard', icon: '▦' }] },
  { active: 'streams', items: [{ to: '/streams', label: 'Streams', icon: '▶' }, { to: '/streams/test1/destinations', label: 'Destinations', icon: '🎯' }, { to: '/recordings', label: 'Recordings', icon: '⏺' }, { to: '/analytics', label: 'Analytics', icon: '📊' }] },
  {
    active: 'settings',
    section: 'Settings',
    items: [
      { to: '/settings/profile', label: 'Profile', icon: '👤' },
      { to: '/settings/security', label: 'Security', icon: '🔒' },
      { to: '/settings/preferences', label: 'Preferences', icon: '⚙' },
    ],
  },
  { active: 'settings', items: [{ to: '/billing', label: 'Billing', icon: '💳' }, { to: '/transactions', label: 'Transactions', icon: '🧾' }] },
];

interface Preferences {
  theme: 'light' | 'dark';
  language: 'en' | 'es';
}

export function PreferencesPage() {
  const ui = useUiStore();
  const [prefs, setPrefs] = useState<Preferences>({ theme: ui.theme, language: 'en' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void http.get<{ preferences?: Partial<Preferences> | null }>('/api/tenant/settings').then((d) => {
      const prefs = d.preferences ?? {};
      if (prefs.theme && prefs.theme !== ui.theme) ui.toggleTheme();
      setPrefs((p) => ({ ...p, ...prefs }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await http.patch('/api/tenant/preferences', prefs);
      ui.toast('success', 'Preferences saved');
    } catch (e) {
      setError((e as Error).message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleTheme = () => {
    const next: 'light' | 'dark' = ui.theme === 'dark' ? 'light' : 'dark';
    ui.toggleTheme();
    setPrefs((p) => ({ ...p, theme: next }));
  };

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="settings-page">
        <h1 className="page-title">Preferences</h1>
        <p className="text-muted mb-4">Customize your workspace.</p>
        {error && <Alert kind="danger">{error}</Alert>}
        <Card title="Theme">
          <div className="flex items-center gap-3">
            <button className={`btn ${ui.theme === 'dark' ? 'secondary' : 'ghost'} sm`} onClick={toggleTheme}>Dark</button>
            <button className={`btn ${ui.theme === 'light' ? 'secondary' : 'ghost'} sm`} onClick={toggleTheme}>Light</button>
          </div>
        </Card>
        <Card title="Language">
          <Select
            label="Language"
            value={prefs.language || 'en'}
            options={[{ value: 'en', label: 'English' }, { value: 'es', label: 'Español' }]}
            onChange={(v) => setPrefs((p) => ({ ...p, language: v as 'en' | 'es' }))}
          />
        </Card>
        <div className="mt-4">
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save preferences'}</Button>
        </div>
      </div>
    </AppLayout>
  );
}
